import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import api from '../services/api';
import { getStoredRole, getTopNavLinks } from '../utils/navigation';

/* ── Paleta ──────────────────────────────────────────────── */
const C = {
  bg:      '#0A0E1A',
  surface: '#111827',
  border:  '#1F2937',
  accent:  '#10B981',
  accentD: '#059669',
  warn:    '#F59E0B',
  danger:  '#EF4444',
  text:    '#F9FAFB',
  muted:   '#6B7280',
};

/* ── Estilos globales inline ─────────────────────────────── */
const styles = {
  page: {
    minHeight: '100vh',
    background: C.bg,
    color: C.text,
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: '0',
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 32px',
    borderBottom: `1px solid ${C.border}`,
    background: C.surface,
  },
  logo: {
    fontSize: 20,
    fontWeight: 700,
    color: C.accent,
    letterSpacing: '-0.5px',
  },
  nav: {
    display: 'flex',
    gap: 24,
  },
  navLink: (active) => ({
    color: active ? C.accent : C.muted,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: active ? 700 : 400,
  }),
  merchantName: {
    fontSize: 13,
    color: C.muted,
    marginTop: 2,
  },
  userBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentD})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
  },
  main: {
    padding: '32px',
    maxWidth: 1280,
    margin: '0 auto',
  },
  heading: {
    fontSize: 26,
    fontWeight: 700,
    marginBottom: 4,
    letterSpacing: '-0.5px',
  },
  subheading: {
    fontSize: 14,
    color: C.muted,
    marginBottom: 32,
  },
  grid4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 20,
    marginBottom: 32,
  },
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: '22px 24px',
  },
  cardLabel: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 10,
  },
  cardValue: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: '-1px',
    marginBottom: 6,
  },
  cardDelta: (positive) => ({
    fontSize: 12,
    color: positive ? C.accent : C.danger,
    fontWeight: 600,
  }),
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 380px',
    gap: 20,
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 20,
    color: C.text,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '8px 12px',
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: '12px 12px',
    fontSize: 13,
    borderBottom: `1px solid ${C.border}22`,
    verticalAlign: 'middle',
  },
  badge: (status) => {
    const map = {
      captured:   { bg: '#10B98122', color: C.accent },
      pending:    { bg: '#F59E0B22', color: C.warn },
      failed:     { bg: '#EF444422', color: C.danger },
      authorized: { bg: '#3B82F622', color: '#60A5FA' },
    };
    const s = map[status] || map.pending;
    return {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      background: s.bg,
      color: s.color,
    };
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 0',
    color: C.muted,
    fontSize: 14,
  },
  logoutBtn: {
    background: 'transparent',
    border: `1px solid ${C.border}`,
    color: C.muted,
    borderRadius: 8,
    padding: '7px 16px',
    fontSize: 13,
    cursor: 'pointer',
  },
};

/* ── Datos de ejemplo para la gráfica ───────────────────── */
const generateChartData = () => {
  const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  return days.map(day => ({
    day,
    ingresos: Math.floor(Math.random() * 8000) + 2000,
    transacciones: Math.floor(Math.random() * 40) + 10,
  }));
};

/* ── Componente principal ────────────────────────────────── */
export default function Dashboard() {
  const navigate = useNavigate();
  const userRole = getStoredRole();
  const topNavLinks = getTopNavLinks(userRole);
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [chartData] = useState(generateChartData());
  const [loading, setLoading] = useState(true);
  const [merchantName, setMerchantName] = useState('Mi Comercio');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      // Cargar pagos recientes
      const resp = await api.get('/merchants/payments?limit=8');
      const data = resp.data;
      setPayments(Array.isArray(data) ? data : []);

      // Calcular stats desde los pagos
      const captured = (Array.isArray(data) ? data : []).filter(p => p.status === 'captured');
      const totalHoy = captured
        .filter(p => new Date(p.created_at).toDateString() === new Date().toDateString())
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

      setStats({
        totalHoy: totalHoy.toFixed(2),
        transaccionesHoy: captured.length,
        tasaAprobacion: data.length > 0
          ? ((captured.length / data.length) * 100).toFixed(1)
          : '0.0',
        pendientes: (Array.isArray(data) ? data : []).filter(p => p.status === 'pending').length,
      });
    } catch {
      // Datos demo si no hay backend
      setStats({
        totalHoy: '4,820.00',
        transaccionesHoy: 23,
        tasaAprobacion: '94.2',
        pendientes: 2,
      });
      setPayments([
        { payment_id: 'pay_001', amount: '250.00', status: 'captured',    customer_name: 'Ana García',    created_at: new Date().toISOString(), payment_method: 'card' },
        { payment_id: 'pay_002', amount: '89.99',  status: 'pending',     customer_name: 'Luis Torres',   created_at: new Date().toISOString(), payment_method: 'card' },
        { payment_id: 'pay_003', amount: '520.00', status: 'captured',    customer_name: 'María Vega',    created_at: new Date().toISOString(), payment_method: 'transfer' },
        { payment_id: 'pay_004', amount: '15.50',  status: 'failed',      customer_name: 'Carlos Ruiz',   created_at: new Date().toISOString(), payment_method: 'card' },
        { payment_id: 'pay_005', amount: '310.00', status: 'authorized',  customer_name: 'Sofía Mendez',  created_at: new Date().toISOString(), payment_method: 'card' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  const fmt = (val) => `$${parseFloat(val || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚡</div>
          <div style={{ color: C.muted, fontSize: 14 }}>Cargando dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Topbar */}
      <div style={styles.topbar}>
        <div>
          <div style={styles.logo}>⚡ Yupay Fintech</div>
          <div style={styles.merchantName}>Portal de Comercios</div>
        </div>
        <nav style={styles.nav}>
          {topNavLinks.map(link => (
            <Link key={link.to} to={link.to} style={styles.navLink(link.to === '/dashboard')}>
              {link.label}
            </Link>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={styles.userBadge}>
            <div style={styles.avatar}>M</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{merchantName}</div>
              <div style={{ fontSize: 11, color: C.muted }}>Activo</div>
            </div>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Salir</button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.heading}>Dashboard</div>
        <div style={styles.subheading}>
          {new Date().toLocaleDateString('es-EC', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>

        {/* KPIs */}
        <div style={styles.grid4}>
          <StatCard label="Recaudado hoy" value={fmt(stats?.totalHoy)} delta="+12.4% vs ayer" positive />
          <StatCard label="Transacciones" value={stats?.transaccionesHoy} delta="+3 vs ayer" positive />
          <StatCard label="Tasa aprobación" value={`${stats?.tasaAprobacion}%`} delta="+0.8%" positive />
          <StatCard label="Pendientes" value={stats?.pendientes} delta={stats?.pendientes > 0 ? 'Requieren atención' : 'Todo al día'} positive={stats?.pendientes === 0} />
        </div>

        {/* Gráfica + Métodos */}
        <div style={styles.grid2}>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Ingresos esta semana</div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.accent} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={C.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="day" tick={{ fill: C.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.muted, fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
                  formatter={v => [`$${v.toLocaleString()}`, 'Ingresos']}
                />
                <Area type="monotone" dataKey="ingresos" stroke={C.accent} strokeWidth={2} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Transacciones por día</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="day" tick={{ fill: C.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                <Bar dataKey="transacciones" fill={C.accent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tabla de pagos recientes */}
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={styles.sectionTitle}>Pagos recientes</div>
            <Link to={topNavLinks.some(link => link.to === '/payments') ? '/payments' : '/dashboard'} style={{ fontSize: 13, color: C.accent, textDecoration: 'none' }}>Ver todos →</Link>
          </div>

          {payments.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div>No hay pagos aún</div>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['ID', 'Cliente', 'Monto', 'Método', 'Hora', 'Estado'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.payment_id}>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12, color: C.muted }}>
                      {p.payment_id?.slice(-8).toUpperCase()}
                    </td>
                    <td style={styles.td}>{p.customer_name || '—'}</td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{fmt(p.amount)}</td>
                    <td style={{ ...styles.td, color: C.muted }}>
                      {p.payment_method === 'card' ? '💳 Tarjeta' : '🏦 Transferencia'}
                    </td>
                    <td style={{ ...styles.td, color: C.muted }}>{fmtTime(p.created_at)}</td>
                    <td style={styles.td}>
                      <span style={styles.badge(p.status)}>
                        {p.status === 'captured' ? 'Cobrado' :
                         p.status === 'pending'  ? 'Pendiente' :
                         p.status === 'failed'   ? 'Fallido' : 'Autorizado'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, delta, positive }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardLabel}>{label}</div>
      <div style={styles.cardValue}>{value}</div>
      <div style={styles.cardDelta(positive)}>
        {positive ? '↑' : '↓'} {delta}
      </div>
    </div>
  );
}
